import { X } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { Summary, Dashboard } from '|/app/services';
import { useBrandCapabilities } from '|/brand';
import { SupportContact, SupportContactLink, SupportMfeModal } from '|/components/support-contact';
import { CheckItOutContext } from '|/context';
import MobileNavigationRailContext from '|/context/mobile-navigation-rail-context';
import { useIsMobileViewport } from '|/hooks/use-is-mobile-viewport';
import mobileStyles from './mobile-navigation-rail.module.css';

interface MobileNavigationRailProps {
  dashboard: Dashboard | null;
  summary: Summary | null;
  children?: React.ReactNode;
}

// ds-modal 6.3 slides the drawer with `translateX(var(--drawer-width))`, where a percentage
// resolves against the drawer's own width rather than the viewport — '80%' would start the panel
// 16vw on screen. Viewport units reproduce the widths the CSS module used before.
const NARROW_DRAWER_WIDTH = '80vw';
const WIDE_DRAWER_WIDTH = '60vw';
const NARROW_BREAKPOINT_PX = 479;

// Support & Contact opens from inside this drawer and has to stack above it, so the drawer sits
// below that modal's own 700 (see support-mfe-modal.tsx) rather than ds-modal's 1300 default.
const DRAWER_Z_INDEX = 600;

const MobileNavigationRail: React.FC<MobileNavigationRailProps> = ({
  dashboard,
  summary,
  children
}) => {
  const { showMobileNavigationRail, setShowMobileNavigationRail } = useContext(
    MobileNavigationRailContext
  );
  const { navigation } = useBrandCapabilities();
  // The snapshot chrome shipped with the pp-check-it-out treatment (TXCC-9916); the live
  // control arm keeps its original support button.
  const { isCheckItOutTreatment } = useContext(CheckItOutContext);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const isNarrowViewport = useIsMobileViewport(NARROW_BREAKPOINT_PX);

  if (dashboard === null || summary === null) {
    return null;
  }

  const handleClose = () => {
    setShowMobileNavigationRail(false);
  };

  return (
    <>
      <EventedModal
        isOpen={showMobileNavigationRail}
        analyticsId={ModalNames.MobileNavigationDrawer}
        onClose={handleClose}
        aria-label="Navigation menu"
        drawerStyle="all"
        drawerSide="right"
        drawerWidth={isNarrowViewport ? NARROW_DRAWER_WIDTH : WIDE_DRAWER_WIDTH}
        zIndex={DRAWER_Z_INDEX}
        classes={{
          container: mobileStyles.navigationDrawer,
          header: mobileStyles.emptyDsHeader
        }}
        closeIcon={false}
        contentPadding={false}
        disableOutsideClose={false}
        disableEscapeClose={false}
      >
        <div className={mobileStyles.modalHeader}>
          {navigation.offersSupportContact && (
            <div className={mobileStyles.supportContactWrapper}>
              {isCheckItOutTreatment ? (
                <SupportContactLink
                  onClick={() => {
                    setIsSupportModalOpen(true);
                  }}
                  onClose={() => {
                    setShowMobileNavigationRail(false);
                  }}
                />
              ) : (
                <SupportContact
                  onClick={() => {
                    setIsSupportModalOpen(true);
                  }}
                  onClose={() => {
                    setShowMobileNavigationRail(false);
                  }}
                />
              )}
            </div>
          )}
          <button
            className={mobileStyles.closeButton}
            onClick={handleClose}
            aria-label="Close menu"
            data-analytics-id="pp_navrail_close"
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>
        <div className={mobileStyles.modalContent}>{children}</div>
      </EventedModal>

      {/* Outside the drawer on purpose: Support & Contact closes the nav menu as it opens, so
          nesting it here would unmount the MFE mid-load. */}
      <SupportMfeModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
        closeMobileNavMenu={() => {
          setShowMobileNavigationRail(false);
        }}
      />
    </>
  );
};

export default MobileNavigationRail;
